import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import mapaService from '../services/mapaService';
import './Mapa.css';

const esTactil = () => ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);

const Portal = ({ children }) => ReactDOM.createPortal(children, document.body);

const Mapa = ({ datosIngreso, recargar, token, alCambiarModal }) => {

  const [lugares, setLugares]             = useState({});
  const [ultimoIngreso, setUltimoIngreso] = useState({ patente:"---", nombre:"Esperando ingreso...", hora:"--:--" });
  const [tooltip, setTooltip]             = useState({ visible:false, x:0, y:0, id:null });
  const [tooltipTactil, setTooltipTactil] = useState({ visible:false, id:null });
  const [modal, setModal]                 = useState({ abierto:false, lugarId:null, pass:"" });

  useEffect(() => {
    if (alCambiarModal) alCambiarModal(modal.abierto);
  }, [modal.abierto, alCambiarModal]);

  const cargarDatos = useCallback(async () => {
    try {
      const data = await mapaService.obtenerMapa();
      const dic  = {};
      (data.lugares || []).forEach(l => dic[l.id_lugar] = l);
      setLugares(dic);
      if (data.ultimo_ingreso) setUltimoIngreso(data.ultimo_ingreso);
    } catch (e) { console.error("Error cargando mapa:", e); }
  }, []);

  useEffect(() => { if (datosIngreso) setUltimoIngreso(datosIngreso); }, [datosIngreso]);

  useEffect(() => {
    cargarDatos();
    const intervalo = setInterval(cargarDatos, 3000);
    return () => clearInterval(intervalo);
  }, [cargarDatos, recargar]);

  const handleLiberarAction = async () => {
    if (!modal.pass) return alert("Clave requerida");
    try {
      await mapaService.liberarLugar(modal.lugarId, modal.pass);
      setModal({ abierto:false, lugarId:null, pass:"" });
      cargarDatos();
      alert("✅ Lugar liberado correctamente");
    } catch (err) {
      alert("⛔ " + (err.response?.data?.error || "Error de autorización"));
    }
  };

  const posicionarTooltip = (clientX, clientY) => {
    const W = 230, H = 210, M = 14;
    const x = clientX + M + W > window.innerWidth  ? clientX - W - M : clientX + M;
    const y = clientY + M + H > window.innerHeight ? clientY - H - M : clientY + M;
    return { x, y };
  };

  // ── SUB-COMPONENTES ──────────────────────────────────────────────────────
  const Celda = ({ id, w=1, h=1, txt=null }) => {
    const info        = lugares[id] || lugares[`E.${id}`];
    const bloqueado   = info?.bloqueado;
    const estaOcupado = info?.ocupado && !bloqueado;

    // --- MAGIA DE COLORES ACTUALIZADA ---
    let claseColor = 'libre';
    if (bloqueado) {
      claseColor = 'bloqueado';
    } else if (estaOcupado) {
      if (info?.es_reserva) { 
        claseColor = 'reservado';
      } else {
        claseColor = 'ocupado';
      }
    }

    return (
      <div
        className={`celda ${claseColor}`}
        style={{
          width:  `calc(var(--cell-size) * ${w})`,
          height: `calc(var(--cell-size) * ${h})`,
          cursor: estaOcupado ? 'pointer' : 'default'
        }}
        onMouseEnter={(e) => {
          if (!estaOcupado || esTactil()) return;
          const { x, y } = posicionarTooltip(e.clientX, e.clientY);
          setTooltip({ visible:true, x, y, id });
        }}
        onMouseMove={(e) => {
          if (!estaOcupado || esTactil()) return;
          const { x, y } = posicionarTooltip(e.clientX, e.clientY);
          setTooltip(prev => ({ ...prev, x, y }));
        }}
        onMouseLeave={() => setTooltip({ visible:false, x:0, y:0, id:null })}
        onTouchStart={(e) => {
          if (!estaOcupado) return;
          e.preventDefault();
          setTooltipTactil({ visible:true, id });
        }}
        onClick={() => {
          if (estaOcupado && !esTactil())
            setModal({ abierto:true, lugarId:id, pass:"" });
        }}
      >
        {txt || id}
      </div>
    );
  };

  const ParDoble = ({ n }) => (
  <div className="par-doble">
    <Celda id={`${n}A`}/><Celda id={`${n}B`}/>
  </div>
  );

  const ZonaAzul = ({ texto, w=1, h=1, vertical=false }) => (
    <div className="azul" style={{ width:`calc(var(--cell-size)*${w})`, height:`calc(var(--cell-size)*${h})` }}>
      <span className={vertical ? "texto-vertical" : ""}>{texto}</span>
    </div>
  );

  const getInfo      = (id) => lugares[id] || lugares[`E.${id}`];
  const datosDesktop = tooltip.id       ? getInfo(tooltip.id)?.ocupado_por       : null;
  const datosTactil  = tooltipTactil.id ? getInfo(tooltipTactil.id)?.ocupado_por : null;

  // Motos: cada tarjeta (E.M1, E.M2, ...) se crea sola al escanearla; se dibujan solo las que están en uso
  const motosEnUso = Object.values(lugares)
    .filter(l => /^E\.M\d+$/.test(l.id_lugar) && l.ocupado)
    .sort((a, b) => Number(a.id_lugar.slice(3)) - Number(b.id_lugar.slice(3)));

  // --- FUNCIÓN DE DECODIFICACIÓN 100% PRECISA ---
  const decodificarDatos = (id, datos) => {
    let patenteFinal = datos.ppu || "---";
    let destinoFinal = datos.destino || "";

    // Aseguramos el formato "E.X"
    const idLimpio = String(id).startsWith('E.') ? String(id) : `E.${id}`;

    // 1. Extraer TODAS las combinaciones [LUGAR:PATENTE]
    // Ejemplo: Encuentra "[E.1:AB12]", "[E.2:CD34]"
    const etiquetas = destinoFinal.match(/\[E\.[0-9A-Z]+:[^\]]+\]/g);

    if (etiquetas) {
      etiquetas.forEach(etiqueta => {
        // Le quitamos los corchetes -> "E.2:CD34"
        const contenido = etiqueta.slice(1, -1); 
        const partes = contenido.split(':'); // partes[0] es "E.2", partes[1] es "CD34"
        
        // Si el lugar coincide con la celda en la que tenemos el mouse, atrapamos su patente
        if (partes[0] === idLimpio) {
          patenteFinal = partes[1];
        }
      });
    }

    // 2. Limpiar la basura visual del destino
    // Reemplaza cualquier cosa que parezca [E.algo:algo] por vacío
    destinoFinal = destinoFinal.replace(/\[E\.[0-9A-Z]+:[^\]]+\]/g, '').trim();
    // Reemplaza la etiqueta de reserva
    destinoFinal = destinoFinal.replace(/\[RESERVA\]/g, '').trim();

    // Si después de limpiar quedó solo una coma o espacio al principio, la borramos
    if (destinoFinal.startsWith(',')) {
      destinoFinal = destinoFinal.substring(1).trim();
    }

    if (!destinoFinal) destinoFinal = "Reserva Autorizada";

    return { patenteFinal, destinoFinal };
  };

  const TooltipBody = ({ id, datos, infoLugar }) => {
    const { patenteFinal, destinoFinal } = decodificarDatos(id, datos);

    return (
      <>
        <div style={{ borderBottom:'1px solid rgba(255,255,255,0.3)', paddingBottom:6, marginBottom:6, fontWeight:'bold', textTransform:'uppercase', color:'#FFB500' }}>
          Estacionamiento {id}
          {infoLugar?.area && <div style={{ fontSize:'10px', color:'#ccc' }}>Sector: {infoLugar.area}</div>}
        </div>
        <div style={{ marginBottom:5 }}>
          <span style={{ opacity:0.7 }}>Nombre:</span><br/>
          <strong>{datos.nombre}</strong>
        </div>
        <div style={{ marginBottom:5 }}>
          <span style={{ opacity:0.7 }}>Cargo:</span><br/>
          <span style={{ background:'white', color:'#002D5C', padding:'1px 6px', borderRadius:3, fontWeight:'bold', fontSize:11 }}>
            {datos.cargo || 'Visita'}
          </span>
        </div>
        <div style={{ marginBottom:5 }}>
          <span style={{ opacity:0.7 }}>Patente:</span><br/>
          <strong style={{ letterSpacing:1, color:'#FFB500' }}>{patenteFinal}</strong>
        </div>
        {destinoFinal && (
          <div><span style={{ opacity:0.7 }}>Destino:</span><br/><i>{destinoFinal}</i></div>
        )}
      </>
    );
  };

  // ── TOOLTIP BODY para el bottom sheet ──
  const TooltipBodyLight = ({ id, datos, infoLugar }) => {
    const { patenteFinal, destinoFinal } = decodificarDatos(id, datos);

    return (
      <>
        <div style={{ borderBottom:'2px solid #FFB500', paddingBottom:8, marginBottom:12, fontWeight:'bold', textTransform:'uppercase', color:'#002D5C', fontSize:16 }}>
          Estacionamiento {id}
          {infoLugar?.area && <div style={{ fontSize:'12px', color:'#666', fontWeight:'normal', marginTop:2 }}>Sector: {infoLugar.area}</div>}
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px 16px', marginBottom:12 }}>
          <div>
            <div style={{ fontSize:11, color:'#888', textTransform:'uppercase', marginBottom:2 }}>Nombre</div>
            <div style={{ fontWeight:'bold', color:'#1a202c' }}>{datos.nombre}</div>
          </div>
          <div>
            <div style={{ fontSize:11, color:'#888', textTransform:'uppercase', marginBottom:2 }}>Patente</div>
            <div style={{ fontWeight:'bold', color:'#002D5C', letterSpacing:1 }}>{patenteFinal}</div>
          </div>
          <div>
            <div style={{ fontSize:11, color:'#888', textTransform:'uppercase', marginBottom:2 }}>Cargo</div>
            <span style={{ background:'#e3f2fd', color:'#002D5C', padding:'2px 8px', borderRadius:4, fontWeight:'bold', fontSize:12 }}>
              {datos.cargo || 'Visita'}
            </span>
          </div>
          {destinoFinal && (
            <div>
              <div style={{ fontSize:11, color:'#888', textTransform:'uppercase', marginBottom:2 }}>Destino</div>
              <div style={{ color:'#555', fontStyle:'italic' }}>{destinoFinal}</div>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div style={{ position:'relative', width:'100%' }}>

      {/* ── TOOLTIP DESKTOP ── */}
      {tooltip.visible && datosDesktop && (
        <Portal>
          <div className="tooltip-flotante" style={{ position:'fixed', top:tooltip.y, left:tooltip.x, zIndex:999999, pointerEvents:'none' }}>
            <TooltipBody id={tooltip.id} datos={datosDesktop} infoLugar={getInfo(tooltip.id)} />
          </div>
        </Portal>
      )}

      {/* ── BOTTOM SHEET TÁCTIL ── */}
      {tooltipTactil.visible && datosTactil && (
        <Portal>
          <div
            className="tooltip-tactil-overlay"
            style={{ position:'fixed', inset:0, zIndex:999998 }}
            onClick={() => setTooltipTactil({ visible:false, id:null })}
          >
            <div className="tooltip-tactil-sheet" onClick={e => e.stopPropagation()}>
              <div className="tooltip-tactil-handle"/>
              <TooltipBodyLight id={tooltipTactil.id} datos={datosTactil} infoLugar={getInfo(tooltipTactil.id)} />
              <div style={{ display:'flex', gap:10, marginTop:4 }}>
                <button
                  onClick={() => {
                    setTooltipTactil({ visible:false, id:null });
                    setModal({ abierto:true, lugarId:tooltipTactil.id, pass:"" });
                  }}
                  style={{ flex:1, padding:14, background:'#002D5C', border:'none', borderRadius:10, color:'white', fontWeight:'bold', fontSize:15, cursor:'pointer' }}
                >LIBERAR</button>
                <button
                  className="tooltip-tactil-cerrar"
                  style={{ flex:1 }}
                  onClick={() => setTooltipTactil({ visible:false, id:null })}
                >CERRAR</button>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {modal.abierto && (
        <Portal>
          <div style={{
            position:        'fixed',
            top:             0,
            left:            0,
            width:           '100vw',
            height:          '100dvh',       
            backgroundColor: 'rgba(0,0,0,0.7)',
            zIndex:          999999,
            display:         'flex',
            justifyContent:  'center',
            alignItems:      'flex-start',   
            paddingTop:      'max(60px, 10dvh)',
            paddingLeft:     '16px',
            paddingRight:    '16px',
            paddingBottom:   '16px',
            boxSizing:       'border-box',
            overflowY:       'auto',         
          }}>
            <div style={{
              background:   '#fff',
              padding:      '28px 24px 24px',
              borderRadius: '16px',
              width:        '100%',
              maxWidth:     '360px',
              textAlign:    'center',
              boxShadow:    '0 8px 40px rgba(0,0,0,0.4)',
              borderTop:    '5px solid #002D5C',
              flexShrink:   0,
            }}>
              <div style={{ fontSize:22, fontWeight:'bold', color:'#002D5C', marginBottom:4 }}>
                🔒 Liberar {modal.lugarId}
              </div>
              <p style={{ fontSize:13, color:'#888', margin:'0 0 20px' }}>
                Ingrese su clave de usuario para confirmar
              </p>

              <input
                type="password"
                inputMode="text"
                autoComplete="current-password"
                style={{
                  width:        '100%',
                  padding:      '14px',
                  marginBottom: '16px',
                  borderRadius: '10px',
                  border:       '2px solid #ccc',
                  textAlign:    'center',
                  fontSize:     '18px',        
                  boxSizing:    'border-box',
                  outline:      'none',
                  transition:   'border-color 0.2s',
                }}
                onFocus={e => e.target.style.borderColor = '#002D5C'}
                onBlur={e  => e.target.style.borderColor = '#ccc'}
                value={modal.pass}
                onChange={e => setModal({ ...modal, pass: e.target.value })}
                autoFocus
                onKeyDown={e => e.key === 'Enter' && handleLiberarAction()}
                placeholder="••••••••"
              />

              <div style={{ display:'flex', gap:10 }}>
                <button
                  onClick={handleLiberarAction}
                  style={{
                    flex:         1,
                    padding:      '14px',
                    background:   '#002D5C',
                    color:        '#fff',
                    border:       'none',
                    borderRadius: '10px',
                    cursor:       'pointer',
                    fontWeight:   'bold',
                    fontSize:     '15px',
                    touchAction:  'manipulation',
                  }}
                >LIBERAR</button>
                <button
                  onClick={() => setModal({ abierto:false, lugarId:null, pass:"" })}
                  style={{
                    flex:         1,
                    padding:      '14px',
                    background:   '#f0f0f0',
                    color:        '#333',
                    border:       '1px solid #ccc',
                    borderRadius: '10px',
                    cursor:       'pointer',
                    fontSize:     '15px',
                    touchAction:  'manipulation',
                  }}
                >CANCELAR</button>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* ── EL MAPA ── */}
      <div className="contenedor-principal">
        <div className="layout-macro">

          {/* SECTOR IZQUIERDO */}
          <div className="flex-row">
            <div className="flex-col">
              <ZonaAzul texto="Bodegas" w={1} h={6} vertical/>
              {[93,92,91,90,89,88,87,86,85,84].map(n => <Celda key={`${n}A`} id={`${n}A`}/>)}
            </div>
            <div className="flex-col">
              {[99,98,97,96,95].map(n => <Celda key={n} id={String(n)}/>)}
              <Celda id="94A"/>
              {[93,92,91,90,89,88,87,86,85,84].map(n => <Celda key={`${n}B`} id={`${n}B`}/>)}
            </div>
          </div>

          {/* SECTOR CENTRAL Y DERECHO */}
          <div className="flex-col">
            <div className="flex-row fila-superior">
              <div className="caja-escala-wrapper"><ZonaAzul texto="Caja Escala" w={2} h={2}/></div>
              <div className="flex-col">
                <ZonaAzul texto="Bodegas" w={8} h={1}/>
                <div className="flex-row">
                  <Celda id="1"/><Celda id="2"/>
                  <Celda id="3" w={4}/>
                  <Celda id="4"/><Celda id="5"/>
                </div>
              </div>
              <div className="flex-row">
                {[6,7,8,9,10,11,12,13,14,15].map(n => <ParDoble key={n} n={n}/>)}
                <ParDoble n={16}/>
              </div>
              <ZonaAzul texto="Gr.Elec" w={0.8} h={2} vertical/>
              <div className="flex-row" style={{ marginLeft:'calc(var(--cell-size)*0.15)' }}>
                {[17,18,19,20,21,22].map(n => <ParDoble key={n} n={n}/>)}
              </div>
            </div>

            <div className="flex-row" style={{ alignItems:'flex-start', position:'relative' }}>
              <div className="flex-col">
                {[83,82,81,80,79,78,77,76,75].map(n => <Celda key={n} id={String(n)}/>)}
              </div>
              <div className="panel-info">
                <h3>ULTIMO INGRESO</h3>
                <div className="dato-fila"><span className="dato-label">Patente:</span><span className="dato-valor">{ultimoIngreso.patente}</span></div>
                <div className="dato-fila"><span className="dato-label">Nombre:</span><span className="dato-valor">{ultimoIngreso.nombre}</span></div>
                <div className="dato-fila"><span className="dato-label">Hora:</span><span className="dato-valor">{ultimoIngreso.hora}</span></div>
              </div>
              <div className="flex-row zona-media-derecha">
                <div className="flex-col">
                  {[23,24,25,26,27,28,29,30,31,32].map(n => <Celda key={n} id={String(n)}/>)}
                  <div style={{ height:'var(--cell-size)' }}/>
                  <div className="flex-row"><Celda id="45"/><Celda id="44"/></div>
                </div>
                <div style={{ width:'calc(var(--cell-size)*0.9)' }}/>
                <div className="flex-col">
                  {[33,34,35,36,37,38,39,40,41,42,43].map(n => <Celda key={n} id={String(n)}/>)}
                </div>
                <div className="flex-col">
                  <ZonaAzul texto="Sala Eléctrica" w={2} h={5} vertical/>
                  <ZonaAzul texto="Bodega SSGG"    w={2} h={5} vertical/>
                  <Celda id="46"/><Celda id="100"/>
                </div>
              </div>

              <div className="zona-motos">
                <div className="zona-motos-titulo">MOTOS ({motosEnUso.length})</div>
                <div className="zona-motos-celdas">
                  {motosEnUso.map(l => <Celda key={l.id_lugar} id={l.id_lugar.slice(2)}/>)}
                  {motosEnUso.length === 0 && <span className="zona-motos-vacia">Sin motos</span>}
                </div>
              </div>
            </div>

            <div className="flex-row fila-inferior">
              {[73,72,71,70,69,68,67].map(n => <Celda key={n} id={String(n)}/>)}
              <Celda id="66" w={3}/>
              {[65,64,63,62,61,60,59,58,57,56,55,54,53,52,51,50,49,48,47].map(n => <Celda key={n} id={String(n)}/>)}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Mapa;